<template>
  <div v-if="isLoading" id="loading-visualization"><div class="lds-ring"><div /><div /><div /><div /></div></div>
  <template v-else-if="!isLoading">
    <template v-if="!currentProject"><h1>-</h1></template>
    <div v-else class="project-home">
      <div class="info-div">
        <div class="top-container">
          <div class="left-section">
            <h1>{{ currentProject.name }}</h1>
            <h2>{{ currentProject.description }}</h2>
            <hr>
            <br>
            <div class="metadata-section">
              <div class="info-row">
                <div class="label">Version:</div>
                <div class="value">{{ currentProject.version }}</div>
              </div>
              <div class="info-row">
                <div class="label">Author:</div>
                <div class="value">{{ currentProject.author }}</div>
              </div>
              <div class="info-row">
                <div class="label">Email:</div>
                <div class="value">{{ currentProject.email }}</div>
              </div>
              <div class="info-row">
                <div class="label">Keywords:</div>
                <div class="value">{{ currentProject.keywords }}</div>
              </div>
              <div class="info-row">
                <div class="label">Help Url:</div>
                <a class="value" :href="currentProject.help_url">{{ currentProject.help_url }}</a>
              </div>
            </div>
          </div>
          <div class="image-div">
            <img :src="imageURL" alt="Project Image">
          </div>
        </div>

        <br>
        <div v-if="permissions?.project_write && !isPublicRoute" class="button-row">
          <button class="button-light" @click="openUpdateProjectModal">Edit Project Details</button>
          <button
            v-if="false && store.state?.user?.user"
            class="button-light"
            :class="{'button-spinner': isLoadingClone}"
            :disabled="isLoadingClone ? true : false"
            @click="handleCloneProject(currentProject.id)"
          >
            Clone Project
          </button>
          <button
            v-if="store.state?.user?.user"
            class="button-light delete"
            @click="router.push({ name: 'recycle' });"
          >
            Move to Recycle Bin
          </button>
        </div>

        <DatasetList :projectID="currentProject?.id" />
        <TrainerList :projectID="currentProject?.id" />
      </div>
    </div>

    <ProjectUpdateModal
      ref="updateProjectModal"
      :project="currentProject"
      @edited-project="handleProjectEdit"
    />
  </template>
</template>

<script setup>
import {
  ref, computed, onMounted, inject,
} from 'vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import DatasetList from '@/components/DatasetComponent/DatasetList.vue';
import TrainerList from '@/components/TrainerComponent/TrainerList.vue';
import ProjectUpdateModal from '@/components/ProjectsComponent/ProjectUpdateModal.vue';

import { useToast } from 'vue-toast-notification';
import { useMobile } from '@/composables/useMobile';
import { useStore } from 'vuex';
import { event } from 'vue-gtag';
import { useRoute, useRouter } from 'vue-router';
import { decodeID } from '@/assets/js/utils.js';

// Props passed from router params
defineProps({
  projectId: String,
});

const isPublic = inject('isPublic', false);
const route = useRoute();
const router = useRouter();

const $toast = useToast();
const { isMobile } = useMobile();
const store = useStore();
// const imageURL = ref("");
const isLoading = ref(false);
const isLoadingClone = ref(false);
const updateProjectModal = ref(null);
const permissions = ref(null);
const imageTimestamp = ref(Date.now());

const currentProject = computed({
  get: () => store.state.projects.currentProject,
  set: (value) => store.commit('projects/setCurrentProject', value),
});

const isPublicRoute = computed(() => {
  return route.path.includes('/public') || Boolean(isPublic) || Boolean(currentProject.value?.is_public);
});

const imageURL = computed(() => {
  if (currentProject.value?.id) {
    return `/resource/image?type=project&id=${currentProject.value.id}&ts=${imageTimestamp.value}`;
  }
  return null;
});

async function getProjectDetails(project_id) {
  const dataConnect = new DatastoreConnect();
  await dataConnect.getProject({ project_id })
    .then(async (data) => {
      if (!data.result) {
        throw data.error.message;
      }
      currentProject.value = data.result;
    })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to get project details: ${error}`,
        type: "error",
        position: "top",
      });
    });
}

onMounted(async () => {
  isLoading.value = true;
  if (route.params.projectId) {
    const decodedId = decodeID(route.params.projectId, 'p');
    await getProjectDetails(decodedId);
  }
  getPermissionsForCurrentUser();
  isLoading.value = false;
});

function openUpdateProjectModal(p) {
  updateProjectModal.value.showModal();
}

async function handleProjectEdit(updatedProject) {
  imageTimestamp.value = Date.now();
  currentProject.value = updatedProject;
}

async function getPermissionsForCurrentUser() {
  const user = store.state?.user?.user;
  if (currentProject.value && user) {
    const dataConnect = new DatastoreConnect();
    await dataConnect.getProjectAccessForSingleUser({
      project_id: currentProject.value.id,
      username: user.username,
    })
      .then((data) => {
        if (data.result) {
          permissions.value = data.result;
        }
      })
      .catch((e) => console.log(e));
  } else {
    permissions.value = null;
  }
}

async function handleCloneProject(id) {
  isLoadingClone.value = true;
  const dataConnect = new DatastoreConnect();
  const result = await dataConnect.cloneProject({ project_id: id })
    .then(async (data) => {
      if (!data.result) {
        throw data.error.message;
      }
      $toast.open({
        queue: true,
        dismissible: true,
        duration: 5000,
        message: `Successfully cloned project`,
        type: "success",
        position: "top",
      });
      isLoadingClone.value = false;
      return data.result;
    })
    .catch((error) => {
      $toast.open({
        queue: true,
        dismissible: true,
        message: `Failed to clone project: ${error}`,
        type: "error",
        position: "top",
      });
      isLoadingClone.value = false;
    });
}

</script>

<style lang="scss" scoped>
@import '@/assets/css/themes/auzone/buttons.scss';

.project-home{
  display: flex;
  margin: 0;
  padding: 20px;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  max-width: 100vw;
  @include full-vh;
  box-sizing: border-box;
}

.info-div {
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  box-sizing: border-box;
}

.top-container {
  display: flex;
  flex-direction: row;
  gap: 20px;
  align-items: flex-start;
  flex-wrap: wrap;
  max-width: 100%;
}

.left-section {
  flex: 1 1 auto;
  min-width: 300px;
  display: flex;
  flex-direction: column;
}

.metadata-container {
  display: flex;
  flex-direction: row;
  gap: 20px;
  align-items: flex-start;
  flex-wrap: wrap;
  max-width: 100%;
}

.metadata-section {
  flex: 1 1 auto;
  min-width: 300px;
  display: flex;
  flex-direction: column;
}

.image-div {
  flex: 0 0 auto;
  max-width: 400px;
  width: 100%;
}

.image-div img {
  width: 100%;
  max-width: 100%;
  height: auto;
  border-radius: 8px;
  display: block;
}

.info-row {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
}

.button-row {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 10px;
}

.label {
  width: 120px;
  min-width: 120px;
  font-weight: 600;
  margin-top:4px;
}
.value {
  font-weight: 300;
  word-break: break-word;
  flex: 1;
}

</style>
